import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast, Toaster } from "sonner";
import { Plus, Edit, Trash2, ShieldCheck, Eye, EyeOff, Pencil } from "lucide-react";

export default function Usuarios() {
  const [users, setUsers] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const cargar = async () => {
    try {
      const [ru, rs, rsec] = await Promise.all([
        api.get("/users"), api.get("/sucursales"), api.get("/meta/secciones"),
      ]);
      setUsers(ru.data); setSucursales(rs.data); setSecciones(rsec.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { cargar(); }, []);

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar usuario?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Eliminado"); cargar(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const sucName = (id) => sucursales.find((s) => s.id === id)?.nombre || "—";

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Administración</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Usuarios</h1>
          <p className="text-sm text-slate-500 mt-1">Crea usuarios y define qué pestañas pueden ver o editar.</p>
        </div>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button data-testid="user-nuevo" className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="w-4 h-4 mr-2" />Nuevo Usuario</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nuevo Usuario</DialogTitle></DialogHeader>
            <UserForm sucursales={sucursales} secciones={secciones} onSaved={() => { setShowNew(false); cargar(); }} onCancel={() => setShowNew(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <Th>Email</Th><Th>Nombre</Th><Th>Rol</Th><Th>Sucursal Default</Th>
                <Th>Permisos</Th><Th>Estado</Th><Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const counts = countPermisos(u);
                return (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <Td className="font-medium">{u.email}</Td>
                    <Td>{u.name}</Td>
                    <Td>
                      <Badge className={u.role === "admin" ? "bg-blue-100 text-blue-800 hover:bg-blue-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>
                        {u.role}
                      </Badge>
                    </Td>
                    <Td>{sucName(u.sucursal_default)}</Td>
                    <Td>
                      {u.role === "admin" ? (
                        <span className="text-xs text-blue-700 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Acceso total</span>
                      ) : (
                        <div className="flex gap-1.5 text-xs">
                          <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded" title="Editar"><Pencil className="w-3 h-3" />{counts.editar}</span>
                          <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-0.5 rounded" title="Solo ver"><Eye className="w-3 h-3" />{counts.ver}</span>
                          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-2 py-0.5 rounded" title="Sin acceso"><EyeOff className="w-3 h-3" />{counts.ninguno}</span>
                        </div>
                      )}
                    </Td>
                    <Td><Badge variant={u.active === false ? "secondary" : "default"}>{u.active === false ? "Inactivo" : "Activo"}</Badge></Td>
                    <Td>
                      <Button variant="ghost" size="icon" onClick={() => setEditing(u)} data-testid={`user-edit-${u.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => eliminar(u.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Usuario</DialogTitle></DialogHeader>
          {editing && <UserForm user={editing} sucursales={sucursales} secciones={secciones} onSaved={() => { setEditing(null); cargar(); }} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const countPermisos = (u) => {
  const p = u.permisos || {};
  const c = { editar: 0, ver: 0, ninguno: 0 };
  Object.values(p).forEach((v) => { c[v] = (c[v] || 0) + 1; });
  return c;
};

function defaultPermisos(secciones) {
  const out = {};
  secciones.forEach((s) => { out[s.key] = s.default_captura; });
  return out;
}

function UserForm({ user, sucursales, secciones, onSaved, onCancel }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    email: user?.email || "",
    name: user?.name || "",
    password: "",
    role: user?.role || "captura",
    sucursal_default: user?.sucursal_default || "",
    active: user?.active !== false,
    permisos: user?.permisos || defaultPermisos(secciones),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPermiso = (key, val) => setForm((f) => ({ ...f, permisos: { ...f.permisos, [key]: val } }));

  // Si cambia el rol a admin, pintar visualmente que tendrá todo
  const isAdminRole = form.role === "admin";

  const aplicarPreset = (preset) => {
    const map = {
      todo_ver: secciones.reduce((acc, s) => ({ ...acc, [s.key]: "ver" }), {}),
      todo_editar: secciones.reduce((acc, s) => ({ ...acc, [s.key]: "editar" }), {}),
      sin_acceso: secciones.reduce((acc, s) => ({ ...acc, [s.key]: "ninguno" }), {}),
      default: defaultPermisos(secciones),
    };
    setForm((f) => ({ ...f, permisos: map[preset] }));
  };

  const guardar = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        role: form.role,
        sucursal_default: form.sucursal_default || null,
        active: form.active,
        permisos: form.role === "admin" ? undefined : form.permisos,
      };
      if (isEdit) {
        if (form.password) payload.password = form.password;
        await api.put(`/users/${user.id}`, payload);
      } else {
        if (!form.password) { toast.error("Contraseña requerida"); setBusy(false); return; }
        await api.post("/users", { ...payload, email: form.email, password: form.password });
      }
      toast.success("Guardado");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
          <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} disabled={isEdit} className="mt-1.5 bg-blue-50 border-blue-200" data-testid="user-email-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Nombre</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-blue-50 border-blue-200" data-testid="user-nombre" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            {isEdit ? "Nueva contraseña (vacío = no cambiar)" : "Contraseña"}
          </Label>
          <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5 bg-blue-50 border-blue-200" data-testid="user-pwd" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Rol</Label>
          <Select value={form.role} onValueChange={(v) => set("role", v)}>
            <SelectTrigger className="mt-1.5 bg-blue-50 border-blue-200" data-testid="user-rol"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin (acceso total)</SelectItem>
              <SelectItem value="captura">Captura (permisos personalizados)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Sucursal por defecto</Label>
          <Select value={form.sucursal_default || "none"} onValueChange={(v) => set("sucursal_default", v === "none" ? "" : v)}>
            <SelectTrigger className="mt-1.5 bg-blue-50 border-blue-200"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Ninguna —</SelectItem>
              {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {isEdit && (
          <div className="flex items-center gap-2 sm:col-span-2 pt-1">
            <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} id="user-active" />
            <Label htmlFor="user-active">Usuario activo</Label>
          </div>
        )}
      </div>

      {/* Sección de permisos */}
      {!isAdminRole && (
        <div className="border-t border-slate-200 pt-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-sm font-semibold flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-600" /> Permisos por pestaña
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Marca <strong>Ver</strong> para que aparezca la pestaña en el menú. Marca también <strong>Editar</strong> si además puede modificar.
              </div>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              <Button type="button" variant="outline" size="sm" onClick={() => aplicarPreset("default")} className="text-xs h-7" data-testid="preset-default">Default captura</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => aplicarPreset("todo_ver")} className="text-xs h-7">Marcar todo ver</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => aplicarPreset("todo_editar")} className="text-xs h-7">Marcar todo editar</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => aplicarPreset("sin_acceso")} className="text-xs h-7">Desmarcar todo</Button>
            </div>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-xs uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Pestaña</th>
                  <th className="px-3 py-2 text-center w-20">Ver</th>
                  <th className="px-3 py-2 text-center w-20">Editar</th>
                </tr>
              </thead>
              <tbody>
                {secciones.map((s) => {
                  const nivel = form.permisos[s.key] || "ninguno";
                  const verCheck = nivel === "ver" || nivel === "editar";
                  const editarCheck = nivel === "editar";
                  return (
                    <tr key={s.key} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`perm-row-${s.key}`}>
                      <td className="px-3 py-2.5">
                        <label htmlFor={`ver-${s.key}`} className="font-medium cursor-pointer select-none">{s.label}</label>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <Checkbox
                          id={`ver-${s.key}`}
                          checked={verCheck}
                          onCheckedChange={(checked) => {
                            // Si destildas Ver, también destilda Editar
                            setPermiso(s.key, checked ? "ver" : "ninguno");
                          }}
                          data-testid={`perm-ver-${s.key}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <Checkbox
                          id={`editar-${s.key}`}
                          checked={editarCheck}
                          onCheckedChange={(checked) => {
                            // Si marcas Editar, automáticamente marca Ver
                            setPermiso(s.key, checked ? "editar" : (verCheck ? "ver" : "ninguno"));
                          }}
                          data-testid={`perm-editar-${s.key}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-slate-500 mt-2 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3 text-amber-700" /> Solo ver</span>
            <span className="inline-flex items-center gap-1"><Pencil className="w-3 h-3 text-emerald-700" /> Ver y editar</span>
            <span className="inline-flex items-center gap-1"><EyeOff className="w-3 h-3 text-slate-500" /> Sin acceso (pestaña oculta)</span>
          </div>
        </div>
      )}

      {isAdminRole && (
        <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-md p-3 text-sm flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" />
          <span>El rol <strong>Admin</strong> tiene acceso total a todas las pestañas.</span>
        </div>
      )}

      <DialogFooter className="pt-2">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={guardar} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="user-guardar">
          {busy ? "Guardando…" : "Guardar"}
        </Button>
      </DialogFooter>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-2.5 ${className}`}>{children}</td>;
